/**
 * @fileoverview Unified Flow Context - Central state management for case ingestion.
 *
 * Manages the entire flow state including:
 * - Current step navigation (0-8)
 * - Matter creation and patching
 * - Audit record for section tracking (found/not_found status)
 * - Form state for Basics and Case Info steps
 *
 * The context is the single source of truth for the ingestion wizard.
 * All step components consume this context via `useUnifiedFlow()`.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/query';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useDuplicateDetection } from '@/components/matter-form/useDuplicateDetection';
import {
  useCreateCaseIngestionImportMutation,
  useGetCaseIngestionImportQuery,
} from '@/services/api/caseIngestionAssistService';
import {
  useAssignCaseManagerMutation,
  useChangeCaseManagerMutation,
  useRemoveCaseManagerMutation,
} from '@/services/api/caseManagerService';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import {
  useCreateIncidentMutation,
  useGetIncidentsQuery,
  useUpdateIncidentMutation,
} from '@/services/api/incidentService';
import {
  useGetMatterDetailsQuery,
  usePatchMatterClientMutation,
  usePatchMatterMutation,
} from '@/services/api/mattersService';
import type { SearchResult } from '@/services/api/searchService';
import { useCreateStaffNoteMutation } from '@/services/api/staffNotesService';
import type {
  CaseIngestionImport,
  CreateCaseIngestionImportRequest,
} from '@/services/types/case-ingestion-assist-types';
import type {
  CaseIngestionResult,
  IngestionSection,
  IngestionSections,
  IngestionSectionStatus,
} from '@/services/types/case-ingestion-types';
import { MatterType, MatterUpdate } from '@/services/types/client-intake-types';

import { getApiErrorMessage } from '@/utils/api-error';
import { extractAttorneyId } from '@/utils/attorney-helpers';

import type { CaseIngestionAssistState } from '../assist/types';
import { type CurrentValueMap, useCaseIngestionAssist } from '../assist/useCaseIngestionAssist';
import {
  CREATION_STEP_COUNT,
  FIRST_INGESTION_STEP_INDEX,
  UNIFIED_STEPS,
} from '../constants';
import {
  useAuditRecordManagement,
  useIncidentSummaryNote,
  useMatterCreation,
  useNavigationFlow,
} from '../hooks';
import { createMatterSchema } from '../schemas';
import type { CreateMatterFormValues, UnifiedFlowPhase } from '../types';
import { createCreationFormDefaults, mapMatterToCreationForm } from '../utils/hydration';
import {
  formatIngestionNote,
  INCIDENT_SUMMARY_HEADER,
} from '../utils/notesParser';

interface CaseIngestionFlowState {
  currentStepIndex: number;
  matterId: string | null;
  matterName: string | null;
  isCreatingMatter: boolean;
  isPatchingCaseInfo: boolean;
  phase: UnifiedFlowPhase;
  /** True when saving before navigating via stepper */
  isSavingStep: boolean;
  /** Audit record with section review status */
  auditRecord: CaseIngestionResult | null;
  /** Loading state for audit record */
  isLoadingAudit: boolean;
}

interface CaseIngestionFlowContextValue {
  state: CaseIngestionFlowState;
  // Navigation
  goToStep: (index: number) => void;
  goNext: () => Promise<void>;
  goBack: () => void;
  /** Request navigation to a step - saves current form first if on audit step */
  requestNavigateToStep: (index: number) => void;
  /** Called by step components after save completes (success or failure) */
  onSaveComplete: (success: boolean) => void;
  // Form
  creationForm: UseFormReturn<CreateMatterFormValues>;
  selectedFirm: { id: string; name: string } | null;
  setSelectedFirm: (firm: { id: string; name: string } | null) => void;
  // Audit record management
  /** Update one or more section statuses in the audit record (batch) */
  updateSectionStatuses: (
    sections: Partial<Record<IngestionSection, IngestionSectionStatus | null>>,
  ) => Promise<void>;
  /** Get current section statuses from the audit record */
  getSectionStatuses: () => IngestionSections;
  /** Check if a specific section has been reviewed */
  isSectionReviewed: (section: IngestionSection) => boolean;
  /** Check if all tracked sections have been reviewed */
  canFinish: boolean;
  // Duplicate detection
  /** Current duplicates detected (from typeahead search) */
  duplicates: SearchResult[];
  /** Whether duplicate confirmation dialog is open */
  showDuplicateDialog: boolean;
  /** Close the duplicate dialog */
  setShowDuplicateDialog: (show: boolean) => void;
  /** Handler for "Create Anyway" button */
  handleConfirmCreateAnyway: () => Promise<void>;
  // Assist panel
  /** Whether the assist panel is open */
  isAssistPanelOpen: boolean;
  /** Toggle the assist panel */
  setIsAssistPanelOpen: (open: boolean) => void;
  /** The import artifact for this matter (null if none exists) */
  importData: CaseIngestionImport | null;
  /** Whether the import query is loading */
  isLoadingImport: boolean;
  /** Derived assist state (computed once, shared by panel + stepper) */
  assistState: CaseIngestionAssistState;
  /** Pending import stored client-side before matter creation */
  pendingImport: CreateCaseIngestionImportRequest | null;
  /** Set pending import data (pre-creation mode) */
  setPendingImport: (data: CreateCaseIngestionImportRequest | null) => void;
  /** Create the matter from current form state (used by "Start from CMS" before goNext) */
  handleCreateMatter: () => Promise<{ matterId: string; clientId: string | undefined }>;
}

const UnifiedFlowContext = createContext<CaseIngestionFlowContextValue | null>(
  null,
);

interface UnifiedFlowProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for the unified case ingestion flow.
 *
 * Handles:
 * - Step navigation with URL sync (?step=stepId)
 * - Matter creation at step 0→1 boundary
 * - Form state management for creation steps
 * - Audit record fetching and section status updates
 * - Hydration when resuming existing matters
 *
 * @param children - Child components to render within the provider
 */
export const UnifiedFlowProvider: React.FC<UnifiedFlowProviderProps> = ({
  children,
}) => {
  const { matterId: matterIdParam } = useParams<{ matterId?: string }>();
  const navigate = useNavigate();

  // Track if we've hydrated from API to prevent re-hydration after creation
  const hasHydrated = useRef(false);
  // Separate hydration ref for incident location (independent data source)
  const hasHydratedIncidentLocation = useRef(false);
  // Separate hydration ref for incident summary (staff note, independent data source)
  const hasHydratedIncidentSummary = useRef(false);
  const [matterId, setMatterId] = useState<string | null>(matterIdParam ?? null);
  const [matterName, setMatterName] = useState<string | null>(null);
  const [selectedFirm, setSelectedFirm] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Duplicate detection state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [pendingCreation, setPendingCreation] = useState(false);

  // Pending import (pre-creation mode)
  const [pendingImport, setPendingImport] = useState<CreateCaseIngestionImportRequest | null>(null);

  // Assist panel state
  const [isAssistPanelOpen, setIsAssistPanelOpen] = useState(false);
  const hasAutoFilledCaseInfo = useRef(false);

  // ==========================================================================
  // Navigation Flow (extracted hook)
  // ==========================================================================
  const {
    currentStepIndex,
    isSavingStep,
    goToStep,
    goBack,
    setCurrentStepIndex,
    requestNavigateToStep,
    onSaveComplete,
  } = useNavigationFlow({
    initialMatterId: matterIdParam ?? null,
    matterId,
  });

  // ==========================================================================
  // Audit Record Management (extracted hook)
  // ==========================================================================
  const {
    auditRecord,
    isLoadingAudit,
    updateSectionStatuses,
    getSectionStatuses,
    isSectionReviewed: checkSectionReviewed,
    canFinish,
  } = useAuditRecordManagement(matterId);

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  // Fetch existing matter data when resuming with /matters/:matterId/ingestion
  const { data: existingMatter } = useGetMatterDetailsQuery(
    matterId ?? skipToken,
  );

  // Case manager mutations - use dedicated endpoints like UpdateMatterForm
  const [assignCaseManager] = useAssignCaseManagerMutation();
  const [changeCaseManager] = useChangeCaseManagerMutation();
  const [removeCaseManager] = useRemoveCaseManagerMutation();

  // Track existing case manager for comparison
  const existingCaseManagerId = existingMatter?.case_manager?.id ?? null;

  // Incident API for incident_location field (get-or-create pattern)
  const { data: incidents } = useGetIncidentsQuery(
    matterId ? { matterId } : skipToken,
  );
  const [createIncident] = useCreateIncidentMutation();
  const [updateIncident] = useUpdateIncidentMutation();
  const [incidentId, setIncidentId] = useState<string | null>(null);

  // Incident summary staff note (saved separately from matter)
  const {
    existingData: existingIncidentSummary,
    saveIncidentSummary,
  } = useIncidentSummaryNote(matterId || '');

  // ==========================================================================
  // Import Artifact (Assist System)
  // ==========================================================================
  const { data: importData, isLoading: isLoadingImport, refetch: refetchImport } =
    useGetCaseIngestionImportQuery(matterId ?? skipToken);

  // Refetch import data when ingestion workflow completes (import_payload just got written)
  const prevIngestionStatus = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const currentStatus = auditRecord?.ingestion_status;
    if (
      prevIngestionStatus.current !== 'completed' &&
      currentStatus === 'completed' &&
      matterId
    ) {
      refetchImport();
    }
    prevIngestionStatus.current = currentStatus;
  }, [auditRecord?.ingestion_status, matterId, refetchImport]);

  // Firms list for matching pending import firm_name
  const { data: firms } = useGetFirmsQuery({});

  // Staff note mutation for write-through incident summary at creation time
  const [createStaffNote] = useCreateStaffNoteMutation();

  // Import mutation for auto-submitting pending import after matter creation
  const [createImportMutation] = useCreateCaseIngestionImportMutation();

  // Derive effectiveImportData: use real importData when available, otherwise
  // synthesize from pendingImport so assist panel and auto-fill work identically.
  const effectiveImportData: CaseIngestionImport | null = useMemo(() => {
    if (importData) return importData;
    if (!pendingImport) return null;
    return {
      matter_id: '',
      imported_at: new Date().toISOString(),
      payload: pendingImport.payload,
    };
  }, [importData, pendingImport]);

  // ==========================================================================
  // Form State
  // ==========================================================================

  const creationForm = useForm<CreateMatterFormValues>({
    resolver: zodResolver<CreateMatterFormValues>(createMatterSchema),
    mode: 'onChange',
    defaultValues: createCreationFormDefaults(),
  });

  // Duplicate detection - context owns the hook since it has form access
  const firstName = creationForm.watch('first_name') || '';
  const lastName = creationForm.watch('last_name') || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const { duplicates } = useDuplicateDetection(fullName);

  const { handleCreateMatter, isCreating } = useMatterCreation(creationForm, {
    setMatterId,
    navigate,
    setCurrentStepIndex,
    markAsHydrated: () => {
      hasHydrated.current = true;
    },
  });

  const [patchMatter, { isLoading: isPatchingCaseInfo }] =
    usePatchMatterMutation();
  const [patchClient] = usePatchMatterClientMutation();

  const phase: UnifiedFlowPhase =
    currentStepIndex < CREATION_STEP_COUNT ? 'creation' : 'ingestion';

  // ==========================================================================
  // Assist State (derived once, shared by panel + stepper)
  // ==========================================================================
  const currentStepId = UNIFIED_STEPS[currentStepIndex]?.id ?? '';

  // Wire real current values from creationForm for case_info fields
  const watchedMatterType = creationForm.watch('matter_type');
  const watchedDateOfLoss = creationForm.watch('date_of_loss');
  const watchedSol = creationForm.watch('statute_of_limitations_date');
  const watchedIncidentLocation = creationForm.watch('incident_location');
  const watchedIncidentSummary = creationForm.watch('incident_summary');
  const watchedCaseId = creationForm.watch('case_id');
  const watchedExternalCmsUrl = creationForm.watch('external_cms_url');

  // Extract specific client fields to avoid recomputing on every RTK Query refetch
  const primaryClient = existingMatter?.clients?.[0];
  const clientEmail = primaryClient?.email;
  const clientPhone = primaryClient?.phone_numbers?.[0]?.phone_number;
  const clientDob = primaryClient?.date_of_birth;
  const clientSsn = primaryClient?.last_four_of_ssn;
  const clientAddressLine1 = primaryClient?.address?.line1;

  const assistCurrentValues: CurrentValueMap = useMemo(() => ({
    // Basics step
    'case_info.firm_name': selectedFirm?.name,
    'case_info.case_type': watchedMatterType,
    'client_info.first_name': firstName,
    'client_info.last_name': lastName,
    // Case Info step
    'case_info.date_of_loss': watchedDateOfLoss,
    'case_info.statute_of_limitations_date': watchedSol,
    'case_info.incident_location': watchedIncidentLocation,
    'case_info.incident_summary': watchedIncidentSummary,
    'case_info.case_id': watchedCaseId,
    'case_info.external_cms_url': watchedExternalCmsUrl,
    // Client Info step
    'client_info.email': clientEmail,
    'client_info.phone_number': clientPhone,
    'client_info.date_of_birth': clientDob,
    'client_info.ssn_last_four': clientSsn,
    'client_info.address': clientAddressLine1,
  }), [
    selectedFirm?.name, watchedMatterType, firstName, lastName,
    watchedDateOfLoss, watchedSol, watchedIncidentLocation, watchedIncidentSummary,
    watchedCaseId, watchedExternalCmsUrl,
    clientEmail, clientPhone, clientDob, clientSsn, clientAddressLine1,
  ]);

  const assistState = useCaseIngestionAssist({
    importData: effectiveImportData,
    isLoading: isLoadingImport && !pendingImport,
    currentStepId,
    currentValues: assistCurrentValues,
  });

  // ==========================================================================
  // Form Hydration (Resume Flow)
  //
  // Populates creationForm when resuming an existing matter via URL.
  // Only runs once per matter load (tracked by hasHydrated ref).
  // ==========================================================================
  useEffect(() => {
    if (!existingMatter || !matterId) return;
    // Skip if already hydrated to prevent overwriting user-entered values
    if (hasHydrated.current) return;

    hasHydrated.current = true;

    // Set matter name for header display
    setMatterName(existingMatter.name || null);

    // Hydrate form using utility function
    const formValues = mapMatterToCreationForm(existingMatter);
    Object.entries(formValues).forEach(([key, value]) => {
      creationForm.setValue(key as keyof CreateMatterFormValues, value);
    });

    // Set selected firm for firm picker component
    if (existingMatter.firm) {
      setSelectedFirm({
        id: existingMatter.firm.id,
        name: existingMatter.firm.name,
      });
    }
  }, [existingMatter, matterId, creationForm]);

  // ==========================================================================
  // Incident Location Hydration (Independent Data Source)
  //
  // Incident data comes from separate API, hydrates independently from matter.
  // Uses its own hydration ref to prevent re-hydration after initial load.
  // ==========================================================================
  useEffect(() => {
    // Skip if already hydrated
    if (hasHydratedIncidentLocation.current) return;
    // Skip if no incidents
    if (!incidents?.length) return;

    const primaryIncident = incidents[0];
    if (primaryIncident) {
      hasHydratedIncidentLocation.current = true;
      setIncidentId(primaryIncident.id);
      creationForm.setValue('incident_location', primaryIncident.location || undefined);
    }
  }, [incidents, creationForm]);

  // ==========================================================================
  // Incident Summary Hydration (Staff Note, Independent Data Source)
  //
  // Incident summary comes from staff notes, hydrates independently from matter.
  // Uses its own hydration ref to prevent re-hydration after initial load.
  // ==========================================================================
  useEffect(() => {
    // Skip if already hydrated
    if (hasHydratedIncidentSummary.current) return;
    // Skip if no existing data
    if (!existingIncidentSummary) return;

    hasHydratedIncidentSummary.current = true;
    creationForm.setValue('incident_summary', existingIncidentSummary);
  }, [existingIncidentSummary, creationForm]);

  // ==========================================================================
  // Auto-Open Assist Panel When Import Arrives
  // ==========================================================================
  const prevImportDataRef = useRef<CaseIngestionImport | null | undefined>(null);
  useEffect(() => {
    // When effectiveImportData transitions from null/undefined -> data, auto-open panel
    if (effectiveImportData && !prevImportDataRef.current) {
      setIsAssistPanelOpen(true);
    }
    prevImportDataRef.current = effectiveImportData;
  }, [effectiveImportData]);

  // ==========================================================================
  // Auto-Fill Case Info Fields From Import
  //
  // Runs once when import data is available. Fills empty form fields
  // with imported values and tracks which fields were auto-filled.
  // ==========================================================================
  useEffect(() => {
    if (hasAutoFilledCaseInfo.current) return;
    if (!effectiveImportData?.payload?.case_info) return;

    hasAutoFilledCaseInfo.current = true;
    const ci = effectiveImportData.payload.case_info;

    const tryFill = (
      field: { presence: string; value: string | null },
      formKey: keyof CreateMatterFormValues,
    ) => {
      if (field.presence !== 'present' || !field.value) return;
      const current = creationForm.getValues(formKey);
      if (current) return; // Don't overwrite existing values
      creationForm.setValue(formKey, field.value);
    };

    tryFill(ci.date_of_loss, 'date_of_loss');
    tryFill(ci.statute_of_limitations_date, 'statute_of_limitations_date');
    tryFill(ci.incident_location, 'incident_location');
    tryFill(ci.incident_summary, 'incident_summary');
    tryFill(ci.case_id, 'case_id');
    tryFill(ci.external_cms_url, 'external_cms_url');
  }, [effectiveImportData, creationForm]);

  // ==========================================================================
  // Auto-Apply Import Data to Matter & Client (via PATCH)
  //
  // Runs once when import data arrives from the CMS ingestion workflow.
  // Patches the matter and primary client with imported values so that
  // step forms hydrate correctly. Only fills empty/null fields.
  // ==========================================================================
  const hasAutoAppliedImport = useRef(false);
  useEffect(() => {
    if (hasAutoAppliedImport.current) return;
    if (!effectiveImportData?.payload) return;
    if (!matterId) return;
    const primaryClientRecord = existingMatter?.clients?.[0];
    if (!primaryClientRecord) return;

    hasAutoAppliedImport.current = true;
    const { payload } = effectiveImportData;

    // Helper: extract present value or null
    const val = (field: { presence: string; value: unknown } | undefined): string | null => {
      if (!field || field.presence !== 'present' || !field.value) return null;
      return String(field.value);
    };

    // Patch matter with case info fields
    const matterPatch: Record<string, unknown> = {};
    const ci = payload.case_info;
    if (val(ci.date_of_loss) && !existingMatter?.date_of_loss) {
      matterPatch.date_of_loss = val(ci.date_of_loss);
    }
    if (val(ci.statute_of_limitations_date) && !existingMatter?.statute_of_limitations_date) {
      matterPatch.statute_of_limitations_date = val(ci.statute_of_limitations_date);
    }
    if (val(ci.case_id) && !existingMatter?.case_id) {
      matterPatch.case_id = val(ci.case_id);
    }
    if (val(ci.external_cms_url) && !existingMatter?.external_cms_url) {
      matterPatch.external_cms_url = val(ci.external_cms_url);
    }

    if (Object.keys(matterPatch).length > 0) {
      patchMatter({ matter_id: matterId, patch: matterPatch as MatterUpdate }).catch(() => {
        // Non-blocking — fields can be filled manually
      });
    }

    // Note: Client info fields (email, phone, DOB, address) are prefilled
    // at the form level in ClientInformationStep, not patched to the DB here.
    // This avoids race conditions with the step's hydration effect.
  }, [effectiveImportData, matterId, existingMatter, patchMatter]);

  // ==========================================================================
  // Auto-Fill Basics Form From Pending Import
  //
  // Fires when pendingImport is set. Fills first_name, last_name,
  // case_type, and firm from the import payload.
  // ==========================================================================
  useEffect(() => {
    if (!pendingImport) return;
    const { payload } = pendingImport;
    const ci = payload.client_info;
    const ca = payload.case_info;

    // First name
    if (ci.first_name.presence === 'present' && ci.first_name.value && !creationForm.getValues('first_name')) {
      creationForm.setValue('first_name', ci.first_name.value);
    }
    // Last name
    if (ci.last_name.presence === 'present' && ci.last_name.value && !creationForm.getValues('last_name')) {
      creationForm.setValue('last_name', ci.last_name.value);
    }
    // Case type — match against MatterType enum
    if (ca.case_type.presence === 'present' && ca.case_type.value && !creationForm.getValues('matter_type')) {
      const matched = matchCaseType(ca.case_type.value);
      if (matched) creationForm.setValue('matter_type', matched, { shouldValidate: true });
    }
    // Firm name — fuzzy match against loaded firms list
    if (ca.firm_name.presence === 'present' && ca.firm_name.value && !selectedFirm) {
      const matched = matchFirm(ca.firm_name.value, firms);
      if (matched) {
        setSelectedFirm(matched);
        creationForm.setValue('firm', matched, { shouldValidate: true });
      }
    }
  }, [pendingImport, creationForm, selectedFirm, setSelectedFirm, firms]);

  // ==========================================================================
  // Case Info & Basics Persistence
  // ==========================================================================

  /**
   * Save Case Info fields by PATCHing the matter.
   * Non-blocking on error — shows toast but still navigates (fields are optional).
   */
  const handleSaveCaseInfo = useCallback(async () => {
    if (!matterId) return;

    const data = creationForm.getValues();

    const patch: Record<string, unknown> = {};

    // Always include date fields - send null when empty to allow clearing
    patch.date_of_loss = data.date_of_loss || null;
    patch.statute_of_limitations_date = data.statute_of_limitations_date || null;
    // incident_location is saved via incident API below (not matter PATCH)
    if (data.contact_client_directly !== undefined)
      patch.contact_client_directly = data.contact_client_directly;
    if (data.simplified_billing_code)
      patch.simplified_billing_code = data.simplified_billing_code;
    if (data.representing_attorney)
      patch.representing_attorney = extractAttorneyId(
        data.representing_attorney,
      );
    if (data.external_cms_url !== undefined)
      patch.external_cms_url = data.external_cms_url || null;
    if (data.external_drive_url !== undefined)
      patch.external_drive_url = data.external_drive_url || null;
    if (data.cms_email !== undefined) patch.cms_email = data.cms_email || null;
    if (data.case_id !== undefined) patch.case_id = data.case_id || null;

    // PATCH non-case-manager fields if there are any
    if (Object.keys(patch).length > 0) {
      try {
        await patchMatter({ matter_id: matterId, patch }).unwrap();
      } catch (error) {
        toast.error(
          getApiErrorMessage(
            error,
            'Some case info could not be saved. You can update it later.',
          ),
        );
      }
    }

    // Handle case manager changes using dedicated endpoints (like UpdateMatterForm)
    const nextCaseManagerId = data.case_manager_id ?? null;
    const didCaseManagerChange = nextCaseManagerId !== existingCaseManagerId;

    if (didCaseManagerChange) {
      try {
        if (!nextCaseManagerId && existingCaseManagerId) {
          // Remove case manager
          await removeCaseManager({
            matterId,
            data: { revert_tasks_to_mp: true },
          }).unwrap();
        } else if (nextCaseManagerId && !existingCaseManagerId) {
          // Assign new case manager
          await assignCaseManager({
            matterId,
            data: {
              case_manager_id: nextCaseManagerId,
              auto_task_assignment: data.auto_task_assignment ?? true,
            },
          }).unwrap();
        } else if (nextCaseManagerId && existingCaseManagerId) {
          // Change case manager
          await changeCaseManager({
            matterId,
            data: {
              new_case_manager_id: nextCaseManagerId,
              auto_task_assignment: data.auto_task_assignment ?? true,
            },
          }).unwrap();
        }
      } catch (error) {
        toast.error(
          getApiErrorMessage(
            error,
            'Failed to update case manager. You can update it later.',
          ),
        );
      }
    }

    // Save incident location via incident API (get-or-create pattern)
    const incidentLocation = data.incident_location?.trim();
    if (incidentLocation !== undefined) {
      try {
        if (incidentId) {
          // Update existing incident
          await updateIncident({
            matterId,
            incidentId,
            incident: { location: incidentLocation || null },
          }).unwrap();
        } else {
          // Create new incident
          const newIncident = await createIncident({
            matterId,
            incident: { location: incidentLocation || null },
          }).unwrap();
          setIncidentId(newIncident.id);
        }
      } catch (error) {
        toast.error(
          getApiErrorMessage(
            error,
            'Incident location could not be saved. You can update it later.',
          ),
        );
      }
    }

    // Save incident summary via staff notes API
    const incidentSummary = data.incident_summary?.trim();
    if (incidentSummary !== undefined) {
      try {
        await saveIncidentSummary(incidentSummary || '');
      } catch (error) {
        toast.error(
          getApiErrorMessage(
            error,
            'Incident summary could not be saved. You can update it later.',
          ),
        );
      }
    }
  }, [
    matterId,
    creationForm,
    patchMatter,
    existingCaseManagerId,
    assignCaseManager,
    changeCaseManager,
    removeCaseManager,
    incidentId,
    createIncident,
    updateIncident,
    saveIncidentSummary,
  ]);

  /**
   * Save Basics fields by PATCHing the matter.
   * Called when user returns to step 0 and clicks Continue.
   * Non-blocking on error — shows toast but still navigates.
   */
  const handleSaveBasics = useCallback(async () => {
    if (!matterId) return;

    const data = creationForm.getValues();
    const patch: Record<string, unknown> = {};

    if (data.firm?.id) patch.firm = data.firm.id;
    // Concatenate first + last name for matter.name
    const clientName = `${data.first_name || ''} ${data.last_name || ''}`.trim();
    if (clientName) patch.name = clientName;
    if (data.matter_type) patch.matter_type = data.matter_type;

    if (Object.keys(patch).length === 0) return;

    try {
      await patchMatter({ matter_id: matterId, patch }).unwrap();
      // Also patch the client's first/last name
      if (existingMatter?.clients?.[0]?.id) {
        await patchClient({
          matter_id: matterId,
          matter_client_id: existingMatter.clients[0].id,
          patch: {
            first_name: data.first_name,
            last_name: data.last_name,
          },
        }).unwrap();
      }
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          'Some basic info could not be saved. You can update it later.',
        ),
      );
    }
  }, [matterId, creationForm, patchMatter, patchClient, existingMatter]);

  // ==========================================================================
  // goNext - Complex navigation logic with form validation and API calls
  // ==========================================================================

  const isGoNextInFlightRef = useRef(false);

  const goNext = useCallback(async () => {
    if (isGoNextInFlightRef.current) return;
    isGoNextInFlightRef.current = true;
    try {
      // Step 0 → 1: Creation boundary — validate basics + create matter
      if (currentStepIndex === CREATION_STEP_COUNT - 1) {
        // If matter already created (went back then forward), save basics and advance
        if (matterId) {
          await handleSaveBasics();
          setCurrentStepIndex(CREATION_STEP_COUNT);
          return;
        }

        // Check for duplicates before creating (soft block with dialog)
        // Skip if user already confirmed via "Create Anyway"
        if (duplicates.length > 0 && !pendingCreation) {
          setShowDuplicateDialog(true);
          return;
        }

        // Validate basics fields before creating
        const isBasicsValid = await creationForm.trigger([
          'firm',
          'first_name',
          'last_name',
          'matter_type',
        ]);
        if (!isBasicsValid) return;

        try {
          const { matterId: newMatterId, clientId: newClientId } = await handleCreateMatter();
          // Reset pending creation flag after successful creation
          setPendingCreation(false);
          // Set matter name for header display (concatenated first + last name)
          const clientFirstName = creationForm.getValues('first_name') || '';
          const clientLastName = creationForm.getValues('last_name') || '';
          setMatterName(`${clientFirstName} ${clientLastName}`.trim() || null);

          // Write-through: PATCH import values to API so it becomes single source of truth
          if (pendingImport && newMatterId) {
            const importPayload = pendingImport.payload;
            const caseInfo = importPayload.case_info;
            const clientInfo = importPayload.client_info;

            // 1. Save CMS import artifact
            try {
              await createImportMutation({ matterId: newMatterId, data: pendingImport }).unwrap();
              setPendingImport(null);  // Only clear after artifact is persisted
            } catch {
              toast.error('Failed to save CMS import. You can re-import later.');
            }

            // 2. PATCH matter with case_info fields
            const matterPatch: Record<string, unknown> = {};
            if (caseInfo.date_of_loss.presence === 'present' && caseInfo.date_of_loss.value)
              matterPatch.date_of_loss = caseInfo.date_of_loss.value;
            if (caseInfo.statute_of_limitations_date.presence === 'present' && caseInfo.statute_of_limitations_date.value)
              matterPatch.statute_of_limitations_date = caseInfo.statute_of_limitations_date.value;
            if (caseInfo.case_id.presence === 'present' && caseInfo.case_id.value)
              matterPatch.case_id = caseInfo.case_id.value;
            if (caseInfo.external_cms_url.presence === 'present' && caseInfo.external_cms_url.value)
              matterPatch.external_cms_url = caseInfo.external_cms_url.value;

            if (Object.keys(matterPatch).length > 0) {
              try {
                await patchMatter({ matter_id: newMatterId, patch: matterPatch }).unwrap();
              } catch {
                toast.error('Some case info could not be saved from import.');
              }
            }

            // 3. PATCH client with client_info fields
            if (newClientId) {
              const clientPatch: Record<string, unknown> = {};
              if (clientInfo.email.presence === 'present' && clientInfo.email.value)
                clientPatch.email = clientInfo.email.value;
              if (clientInfo.date_of_birth.presence === 'present' && clientInfo.date_of_birth.value)
                clientPatch.date_of_birth = clientInfo.date_of_birth.value;
              if (clientInfo.ssn_last_four.presence === 'present' && clientInfo.ssn_last_four.value)
                clientPatch.last_four_of_ssn = clientInfo.ssn_last_four.value;
              if (clientInfo.phone_number.presence === 'present' && clientInfo.phone_number.value)
                clientPatch.phone_numbers = [{ phone_number: clientInfo.phone_number.value }];
              if (clientInfo.address.presence === 'present' && clientInfo.address.value)
                clientPatch.address = clientInfo.address.value;

              if (Object.keys(clientPatch).length > 0) {
                try {
                  await patchClient({
                    matter_id: newMatterId,
                    matter_client_id: newClientId,
                    patch: clientPatch,
                  }).unwrap();
                } catch {
                  toast.error('Some client info could not be saved from import.');
                }
              }
            }

            // 4. Create incident with location + summary
            const incidentLocation = caseInfo.incident_location.presence === 'present' ? caseInfo.incident_location.value : null;
            const incidentSummary = caseInfo.incident_summary.presence === 'present' ? caseInfo.incident_summary.value : null;
            if (incidentLocation || incidentSummary) {
              try {
                const newIncident = await createIncident({
                  matterId: newMatterId,
                  incident: { location: incidentLocation || null },
                }).unwrap();
                setIncidentId(newIncident.id);
                if (incidentSummary) {
                  await createStaffNote({
                    matterId: newMatterId,
                    note: { content: formatIngestionNote(INCIDENT_SUMMARY_HEADER, incidentSummary) },
                  }).unwrap();
                }
              } catch {
                toast.error('Incident details could not be saved from import.');
              }
            }

          }
        } catch {
          // Error already handled in handleCreateMatter
          setPendingCreation(false);
        }
        return;
      }

      // Step 1 → 2: Case Info → first audit-tracked step (patch matter)
      if (currentStepIndex === 1) {
        await handleSaveCaseInfo();
        setCurrentStepIndex(FIRST_INGESTION_STEP_INDEX);
        return;
      }

      // Steps 2-6: Advance within ingestion phase
      if (currentStepIndex < UNIFIED_STEPS.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } finally {
      isGoNextInFlightRef.current = false;
    }
  }, [
    currentStepIndex,
    matterId,
    creationForm,
    handleCreateMatter,
    handleSaveBasics,
    handleSaveCaseInfo,
    setCurrentStepIndex,
    duplicates,
    pendingCreation,
    pendingImport,
    createImportMutation,
    patchMatter,
    patchClient,
    createIncident,
    createStaffNote,
  ]);

  // ==========================================================================
  // Duplicate Detection Handlers
  // ==========================================================================

  /**
   * Handler for "Create Anyway" button in duplicate confirmation dialog.
   * Sets pendingCreation flag to bypass duplicate check on retry.
   */
  const handleConfirmCreateAnyway = useCallback(async () => {
    setShowDuplicateDialog(false);
    setPendingCreation(true);
    // goNext will be called again by the user clicking Continue
    // The pendingCreation flag will bypass the duplicate check
  }, []);

  // ==========================================================================
  // Context Value
  // ==========================================================================

  const contextValue = useMemo<CaseIngestionFlowContextValue>(
    () => ({
      state: {
        currentStepIndex,
        matterId,
        matterName,
        isCreatingMatter: isCreating,
        isPatchingCaseInfo,
        phase,
        isSavingStep,
        auditRecord: auditRecord || null,
        isLoadingAudit,
      },
      // Navigation
      goToStep,
      goNext,
      goBack,
      requestNavigateToStep,
      onSaveComplete,
      // Form
      creationForm,
      selectedFirm,
      setSelectedFirm,
      // Audit record management
      updateSectionStatuses,
      getSectionStatuses,
      isSectionReviewed: checkSectionReviewed,
      canFinish,
      // Duplicate detection
      duplicates,
      showDuplicateDialog,
      setShowDuplicateDialog,
      handleConfirmCreateAnyway,
      // Assist panel
      isAssistPanelOpen,
      setIsAssistPanelOpen,
      importData: effectiveImportData,
      isLoadingImport,
      assistState,
      pendingImport,
      setPendingImport,
      handleCreateMatter,
    }),
    [
      currentStepIndex,
      matterId,
      matterName,
      isCreating,
      isPatchingCaseInfo,
      phase,
      isSavingStep,
      auditRecord,
      isLoadingAudit,
      goToStep,
      goNext,
      goBack,
      requestNavigateToStep,
      onSaveComplete,
      creationForm,
      selectedFirm,
      updateSectionStatuses,
      getSectionStatuses,
      checkSectionReviewed,
      canFinish,
      duplicates,
      showDuplicateDialog,
      handleConfirmCreateAnyway,
      isAssistPanelOpen,
      effectiveImportData,
      isLoadingImport,
      assistState,
      pendingImport,
      handleCreateMatter,
    ],
  );

  return (
    <UnifiedFlowContext.Provider value={contextValue}>
      {children}
    </UnifiedFlowContext.Provider>
  );
};

// =============================================================================
// Helper Functions
// =============================================================================

/** Case type: case-insensitive exact match against MatterType enum values */
function matchCaseType(importedType: string): MatterType | null {
  const normalized = importedType.trim().toLowerCase();
  return Object.values(MatterType).find((t) => t.toLowerCase() === normalized) ?? null;
}

/** Firm: case-insensitive substring match against the firms list */
function matchFirm(
  importedName: string,
  firms: { id: string; name: string }[] | undefined,
): { id: string; name: string } | null {
  if (!firms?.length) return null;
  const normalized = importedName.trim().toLowerCase();
  return firms.find((f) => f.name.toLowerCase() === normalized) ?? null;
}

/**
 * Hook to access the unified flow context.
 *
 * Provides access to:
 * - `state` - Current flow state (step index, matterId, phase, audit record)
 * - `goNext/goBack/goToStep` - Navigation functions
 * - `creationForm` - React Hook Form for Basics/Case Info
 * - `updateSectionStatuses` - Update audit record sections (batch)
 * - `getSectionStatuses` - Get current section statuses
 * - `canFinish` - Whether all required sections are reviewed
 *
 * @throws Error if used outside UnifiedFlowProvider
 */
export const useUnifiedFlow = () => {
  const context = useContext(UnifiedFlowContext);
  if (!context) {
    throw new Error('useUnifiedFlow must be used within UnifiedFlowProvider');
  }
  return context;
};
